import { Seo } from '@/components/Seo';
import { JsonLd, makeWebApplicationSchema, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { TemplateGallery } from '@/components/TemplateGallery';
import { Link } from 'react-router-dom';
import { User, Download, Shield, FileText, Sparkles } from 'lucide-react';

const faqs = [
  {
    question: 'Is ai-free-resume really free?',
    answer:
      'Yes. You can build, edit, and download your resume as a PDF without paying. The AI rewrite feature includes 2 free uses per browser ID.',
  },
  {
    question: 'Do I need to create an account?',
    answer:
      'No. Your resume data is stored locally in your browser. No email, password, or sign-up is required.',
  },
  {
    question: 'Is my data private?',
    answer:
      'Yes. By default, your resume never leaves your device. We only send data to our AI provider when you click AI Rewrite, and we do not store it on our servers.',
  },
  {
    question: 'Can ATS systems read these resumes?',
    answer:
      'Yes. Our templates use clean typography and standard section headings that most applicant tracking systems can parse easily.',
  },
  {
    question: 'Can I use this if I have no work experience?',
    answer:
      'Yes. We have a dedicated resume builder for students and first-time job seekers with guidance on coursework, skills, projects, and volunteer work.',
  },
];

const features = [
  {
    icon: User,
    title: 'No Sign-Up Required',
    desc: 'Start building your resume right away. No email, no account, no password to remember.',
  },
  {
    icon: Download,
    title: 'Free PDF Download',
    desc: 'Download your finished resume as a PDF at no cost. No hidden fees, no paywall.',
  },
  {
    icon: Shield,
    title: 'Private by Default',
    desc: 'Your information is processed in your browser. We do not store your resume on our servers.',
  },
  {
    icon: FileText,
    title: 'ATS-Friendly Templates',
    desc: 'Clean, single-column layouts that work well with most applicant tracking systems.',
  },
  {
    icon: Sparkles,
    title: '2 Free AI Rewrites',
    desc: 'Each browser ID gets 2 free AI-powered bullet rewrites. Upgrade to Pro for 50 per day.',
  },
];

function ResumePreview() {
  return (
    <div className="mx-auto max-w-[420px] lg:mx-0 lg:ml-auto lg:max-w-[480px]">
      {/* Live Preview badge */}
      <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-[#00D4AA] px-3 py-1 text-xs font-semibold text-[#0A2540]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#0A2540]" />
        Live Preview
      </div>

      {/* Browser window */}
      <div className="hero-resume-v5 overflow-hidden rounded-[24px] bg-white shadow-[0_32px_64px_-16px_rgba(0,0,0,0.35)]">
        {/* Browser title bar */}
        <div className="flex items-center gap-1.5 border-b border-[#E2E8F0] px-4 py-3">
          <div className="h-2.5 w-2.5 rounded-full bg-[#EF4444]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#F59E0B]" />
          <div className="h-2.5 w-2.5 rounded-full bg-[#10B981]" />
          <div className="ml-3 rounded bg-[#F1F5F9] px-3 py-1 text-xs text-[#718096]">
            Alex_Johnson_Resume.pdf
          </div>
        </div>

        {/* Resume content */}
        <div className="p-5 text-left">
          <h3 className="text-lg font-bold text-[#0A2540]">Alex Johnson</h3>
          <p className="text-xs text-[#718096]">
            alex.johnson@email.com · (555) 123-4567 · New York, NY
          </p>

          <div className="mt-3 space-y-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#00D4AA]">
                Professional Summary
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-[#4A5568]">
                Marketing graduate with hands-on experience in digital campaigns and social media analytics.
              </p>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#00D4AA]">
                Experience
              </p>
              <p className="mt-0.5 text-xs font-semibold text-[#0A2540]">
                Marketing Intern — ABC Corp
              </p>
              <p className="text-[10px] text-[#718096]">Jun 2024 – Aug 2024</p>
              <ul className="mt-0.5 list-disc pl-4 text-xs text-[#4A5568]">
                <li>Managed social media calendar across 3 channels</li>
                <li>Assisted in email marketing campaigns</li>
              </ul>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#00D4AA]">
                Education
              </p>
              <p className="mt-0.5 text-xs font-semibold text-[#0A2540]">
                B.A. Marketing — State University
              </p>
              <p className="text-[10px] text-[#718096]">2021 – 2025</p>
            </div>

            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#00D4AA]">
                Skills
              </p>
              <p className="mt-0.5 text-xs text-[#4A5568]">
                Social Media, Google Analytics, Content Strategy, SEO
              </p>
            </div>
          </div>
        </div>

        {/* PDF ready badge */}
        <div className="flex items-center justify-center border-t border-[#E2E8F0] py-2">
          <span className="text-[11px] font-medium text-[#00D4AA]">✓ PDF ready</span>
        </div>
      </div>
    </div>
  );
}

export function Home() {
  return (
    <>
      <Seo
        title="Free Resume Builder | AI-Powered, No Sign-Up, Instant PDF"
        description="Build a free, ATS-friendly resume in minutes. No sign-up required. AI-powered bullet rewriter, instant PDF download, and privacy-first local storage."
        path="/"
      />
      <JsonLd schema={makeWebApplicationSchema()} />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'Home', path: '/' }])} />

      {/* Hero Section — Left/Right split */}
      <section className="hero-glow-v5 overflow-hidden pt-6 md:pt-8 lg:pt-10 pb-10 md:pb-12 lg:pb-14">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-start lg:gap-10">
            {/* Left: Copy */}
            <div className="max-w-[540px] text-center lg:flex-1 lg:text-left">
              <span className="inline-flex items-center rounded-full bg-[#00D4AA]/15 px-3 py-1 text-xs font-semibold text-[#00D4AA] ring-1 ring-[#00D4AA]/20 md:px-4 md:py-1.5 md:text-sm">
                100% Free Resume Builder
              </span>
              <h1 className="text-balance mt-3 text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white md:text-[44px] lg:text-[52px]">
                Free Resume Builder — No Sign-Up, Instant PDF
              </h1>
              <p className="mx-auto mt-3 max-w-[520px] text-base text-white/75 md:text-lg lg:mx-0">
                Create a professional, ATS-friendly resume in minutes. No account needed. No hidden fees. Your data stays in your browser.
              </p>
              <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
                <Link
                  to="/app/create"
                  className="btn-primary inline-flex items-center justify-center rounded-[10px] px-6 py-3.5 text-sm font-semibold md:px-8 md:py-4 md:text-base"
                >
                  Create My Free Resume
                </Link>
                <Link
                  to="/resume-templates"
                  className="inline-flex items-center justify-center rounded-[10px] border border-white/[0.18] bg-white/[0.08] px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/[0.14] md:px-8 md:py-4 md:text-base"
                >
                  See Templates
                </Link>
              </div>
              <p className="mt-3 text-xs text-white/55 md:text-sm">
                No credit card. No signup. Privacy-first.
              </p>
            </div>

            {/* Right: Resume preview */}
            <div className="w-full max-w-[420px] lg:max-w-[460px] lg:w-[45%] lg:pt-2">
              <ResumePreview />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section — glass cards */}
      <section className="bg-[#0A2540] py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="glass-card-v5"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-[#00D4AA]/15">
                  <f.icon size={24} className="text-[#00D4AA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-white/75">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Template Preview Section — dark background */}
      <section className="bg-[#0A2540] py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="inline-block rounded-full bg-[#00D4AA]/15 px-4 py-1.5 text-sm font-semibold text-[#00D4AA]">
              Clean, ATS-friendly templates
            </span>
            <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl">
              Resume Templates That Get You Hired
            </h2>
            <p className="mx-auto mt-4 max-w-[680px] text-lg text-white/75">
              All templates are single-column, recruiter-friendly, and free to download.
            </p>
          </div>

          <div className="mt-12">
            <TemplateGallery linkTo="/app/create" featuredId="modern-clean" darkMode />
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/resume-templates"
              className="inline-flex items-center justify-center rounded-[10px] border border-white/[0.18] bg-white/[0.08] px-7 py-3.5 text-base font-semibold text-white hover:bg-white/[0.14]"
            >
              View all templates
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section — dark background */}
      <section className="bg-[#0D2E4D] py-20 md:py-28">
        <div className="mx-auto max-w-[1000px] px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white md:text-4xl">
              How to Build Your Resume in 3 Steps
            </h2>
            <p className="mx-auto mt-4 max-w-[640px] text-lg text-white/75">
              From blank page to ready-to-send resume in under 10 minutes.
            </p>
          </div>

          <div className="relative mt-12 grid gap-8 md:grid-cols-3">
            {[
              {
                title: 'Choose a template',
                desc: 'Pick a clean, ATS-friendly layout that matches your experience level.',
              },
              {
                title: 'Fill in your details',
                desc: 'Add your experience, education, skills, and a short summary.',
              },
              {
                title: 'Download your PDF',
                desc: 'Preview your resume and download it as a polished PDF in one click.',
              },
            ].map((s, idx) => (
              <div key={s.title} className="relative text-center">
                {/* Dashed connector line between steps (desktop, after first two) */}
                {idx < 2 && <div className="step-line-v5" />}
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#00D4AA] text-base font-bold text-[#0A2540]">
                  {idx + 1}
                </div>
                <h3 className="mt-5 text-xl font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-white/75">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section — light background */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">Frequently Asked Questions</h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Get a Resume That Works"
        subtitle="No sign-up. No hidden fees. No spam."
        buttonText="Create My Free Resume"
        buttonTo="/app/create"
      />
    </>
  );
}

export default Home;
